import axios from 'axios';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft } from 'react-feather';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ConfirmModal from '../components/confirm-modal';
import { InvoiceStatus } from '../enums/invoice-status';
import { InvoiceType } from '../enums/invoice-type';
import { CreateRectificationInvoiceDto } from '../interfaces/create-rectification-invoice.dto';
import { HttpExceptionDto } from '../interfaces/http-exception.dto';
import { Invoice } from '../interfaces/invoice';
import { InvoiceTotals } from '../interfaces/invoice-totals';
import { UpdateRectificationInvoiceDto } from '../interfaces/update-rectification-invoice.dto';
import { api } from '../services/api';
import { formatNumber, roundTwoDecimals } from '../services/helpers';
import myToastr from '../services/toastr';

interface FormValues {
  date: string;
  manualProducts: {
    dbId: number;
    units: number;
    maxUnits: number;
  }[];
  observations: string;
  notes: string;
  products: {
    dbId: number;
    units: number;
    maxUnits: number;
  }[];
}

const NewRectificationInvoiceView = () => {
  const navigate = useNavigate();
  const { id, rectificationId } = useParams<{ id: string; rectificationId: string }>();
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rectificationInvoice, setRectificationInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const {
    control,
    formState: { errors },
    getValues,
    register,
    reset,
    trigger,
  } = useForm<FormValues>({
    defaultValues: {
      date: moment().format('YYYY-MM-DD'),
      products: [],
      manualProducts: [],
    },
    reValidateMode: 'onChange',
    mode: 'onChange',
  });
  const { fields: productFields } = useFieldArray({
    control,
    name: 'products',
  });
  const { fields: manualProductFields } = useFieldArray({
    control,
    name: 'manualProducts',
  });
  const products = useWatch({
    control,
    name: 'products',
  });
  const manualProducts = useWatch({
    control,
    name: 'manualProducts',
  });
  // Las organizaciones minoristas no usan recargo de equivalencia en las facturas
  const EQUIVALENCE_SURCHARGE = invoice?.organizationClient?.equivalenceSurcharge ? parseFloat(process.env.REACT_APP_EQUIVALENCE_SURCHARGE!) : 0;
  const invoiceTotals = useMemo(() => {
    const it: InvoiceTotals = {
      units: 0,
      taxBase: 0,
      equivalenceSurcharge: 0,
      vat: 0,
      total: 0,
    };
    products
      .filter((f) => !Number.isNaN(f.units) && f.units > 0 && f.units <= f.maxUnits)
      .forEach((field) => {
        const invoiceProduct = invoice!.invoiceProducts.find((p) => p.id === field.dbId)!;
        const units = field.units;
        const amount = invoiceProduct.amount;
        const discountPercentage = invoiceProduct.discount || 0;
        const vatPercentage = invoiceProduct.vatPercentage || 0;
        const taxBase = amount * units * (1 - discountPercentage / 100);
        const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
        const vatAmount = (taxBase * vatPercentage) / 100;
        it.units += units;
        it.taxBase += taxBase;
        it.equivalenceSurcharge += equivalenceSurchargeAmount;
        it.vat += vatAmount;
        it.total += taxBase + equivalenceSurchargeAmount + vatAmount;
      });
    manualProducts
      .filter((f) => !Number.isNaN(f.units) && f.units > 0 && f.units <= f.maxUnits)
      .forEach((field) => {
        const extendedManualInvoiceProductDto = invoice!.manualInvoiceProducts.find((p) => p.id === field.dbId)!;
        const units = field.units;
        const amount = extendedManualInvoiceProductDto.amount;
        const discountPercentage = extendedManualInvoiceProductDto.discount || 0;
        const vatPercentage = extendedManualInvoiceProductDto.vatPercentage || 0;
        const taxBase = amount * units * (1 - discountPercentage / 100);
        const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
        const vatAmount = (taxBase * vatPercentage) / 100;
        it.units += units;
        it.taxBase += taxBase;
        it.equivalenceSurcharge += equivalenceSurchargeAmount;
        it.vat += vatAmount;
        it.total += taxBase + equivalenceSurchargeAmount + vatAmount;
      });
    return {
      units: -it.units,
      taxBase: -roundTwoDecimals(it.taxBase),
      equivalenceSurcharge: -roundTwoDecimals(it.equivalenceSurcharge),
      vat: -roundTwoDecimals(it.vat),
      total: -roundTwoDecimals(it.total),
    };
  }, [invoice, EQUIVALENCE_SURCHARGE, products, manualProducts]);

  useEffect(() => {
    getData();
  }, [id, rectificationId]);

  useEffect(() => {
    if (invoice || rectificationInvoice) {
      resetForm();
    }
  }, [invoice, rectificationInvoice]);

  const getInvoice = async () => {
    const invoiceId = parseInt(id!);
    if (isNaN(invoiceId) || invoiceId <= 0) {
      myToastr.error('Factura no encontrada');
      navigate('/facturas');
      return;
    }
    try {
      const retrievedInvoice = await api.getInvoice(invoiceId);
      setInvoice(retrievedInvoice);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 404) {
          myToastr.error('Factura no encontrada');
          navigate('/facturas');
          return;
        }
        if (err.response.status === 403) {
          myToastr.error('No tienes permiso para ver esta factura');
          navigate('/facturas');
          return;
        }
      }
      const error = err as Error;
      myToastr.error(error.message);
      navigate('/facturas');
    }
  };

  const getRectificationInvoice = async () => {
    const rectificationInvoiceId = parseInt(rectificationId!);
    if (isNaN(rectificationInvoiceId) || rectificationInvoiceId <= 0) {
      myToastr.error('Factura rectificativa no encontrada');
      navigate('/facturas');
      return;
    }
    try {
      const retrievedInvoice = await api.getInvoice(rectificationInvoiceId);
      setRectificationInvoice(retrievedInvoice);
      const invoiceId = parseInt(id!);
      if (retrievedInvoice.parentInvoiceId !== invoiceId) {
        myToastr.error('La factura rectificativa no pertenece a la factura seleccionada');
        navigate('/facturas');
        return;
      }
      if (retrievedInvoice.invoiceType !== InvoiceType.Rectification) {
        myToastr.error('Factura rectificativa no encontrada');
        navigate('/facturas');
        return;
      }
      if (retrievedInvoice.invoiceStatus === InvoiceStatus.Posted) {
        myToastr.error('No se puede modificar una factura rectificativa contabilizada. Si quieres editarla, crea una nueva factura rectificativa.');
        navigate(`/factura/${invoiceId}`);
        return;
      }
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        if (err.response.status === 404) {
          myToastr.error('Factura rectificativa no encontrada');
          navigate('/facturas');
          return;
        }
        if (err.response.status === 403) {
          myToastr.error('No tienes permiso para ver esta factura rectificativa');
          navigate('/facturas');
          return;
        }
      }
      const error = err as Error;
      myToastr.error(error.message);
      navigate('/facturas');
    }
  };

  const resetForm = () => {
    if (!invoice) {
      return;
    }
    const formValues: FormValues = {
      date: rectificationInvoice ? moment(rectificationInvoice.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD'),
      manualProducts: [],
      notes: rectificationInvoice?.notes || '',
      observations: rectificationInvoice?.observations || '',
      products: [],
    };
    const validRectificationInvoices = invoice.rectifyingInvoices.filter((ri) => ri.invoiceStatus === InvoiceStatus.Posted);
    invoice!.invoiceProducts.forEach((invoiceProduct) => {
      const maxUnits = Math.max(
        validRectificationInvoices.reduce((acc, curr) => {
          const rectifyingProduct = curr.invoiceProducts.find((p) => p.productId === invoiceProduct.productId);
          return acc + (rectifyingProduct ? rectifyingProduct.units : 0);
        }, invoiceProduct.units),
        0,
      );
      if (maxUnits === 0) {
        return;
      }
      const currentUnits = Math.abs(rectificationInvoice?.invoiceProducts.find((p) => p.productId === invoiceProduct.productId)?.units || 0);
      formValues.products.push({
        dbId: invoiceProduct.id,
        units: currentUnits,
        maxUnits,
      });
    });
    invoice!.manualInvoiceProducts.forEach((manualProduct) => {
      const maxUnits = Math.max(
        validRectificationInvoices.reduce((acc, curr) => {
          const rectifyingManualProduct = curr.manualInvoiceProducts.find((p) => p.name === manualProduct.name);
          return acc + (rectifyingManualProduct ? rectifyingManualProduct.units : 0);
        }, manualProduct.units),
        0,
      );
      if (maxUnits === 0) {
        return;
      }
      const currentUnits = Math.abs(rectificationInvoice?.manualInvoiceProducts.find((p) => p.name === manualProduct.name)?.units || 0);
      formValues.manualProducts.push({
        dbId: manualProduct.id,
        units: currentUnits,
        maxUnits,
      });
    });
    reset(formValues);
  };

  const getData = async () => {
    setLoading(true);
    await getInvoice();
    if (rectificationId) {
      await getRectificationInvoice();
    }
    setLoading(false);
  };

  const onSave = async (invoiceStatus: InvoiceStatus) => {
    if (!(await trigger())) {
      return;
    }
    const formValues = getValues();
    const invoiceProducts = formValues.products
      .filter((p) => p.units > 0)
      .map((p) => ({
        id: p.dbId,
        units: p.units,
      }));
    const manualInvoiceProducts = formValues.manualProducts
      .filter((p) => p.units > 0)
      .map((p) => ({
        id: p.dbId,
        units: p.units,
      }));
    if (invoiceProducts.length === 0 && manualInvoiceProducts.length === 0) {
      myToastr.error('Debes rectificar al menos un producto');
      return;
    }
    if (rectificationId) {
      try {
        const payload: UpdateRectificationInvoiceDto = {
          date: formValues.date,
          invoiceProducts,
          invoiceStatus,
          manualInvoiceProducts,
          notes: formValues.notes,
          observations: formValues.observations,
        };
        await api.updateRectificationInvoice(parseInt(rectificationId), payload);
        myToastr.success('Factura rectificativa actualizada correctamente');
        if (invoiceStatus === InvoiceStatus.Posted) {
          navigate(`/factura/${rectificationId}`);
        } else {
          reset(formValues);
        }
      } catch (e: any) {
        const httpExceptionDto = e.response.data as HttpExceptionDto;
        myToastr.error(httpExceptionDto.message);
      }
    } else {
      try {
        const payload: CreateRectificationInvoiceDto = {
          date: formValues.date,
          invoiceId: invoice!.id,
          invoiceProducts,
          invoiceStatus,
          manualInvoiceProducts,
          notes: formValues.notes,
          observations: formValues.observations,
        };
        const newRectificationInvoiceId = await api.createRectificationInvoice(payload);
        if (invoiceStatus === InvoiceStatus.Posted) {
          myToastr.success('Factura rectificativa contabilizada correctamente');
          navigate(`/factura/${newRectificationInvoiceId}`);
          return;
        }
        myToastr.success('Factura rectificativa en estado borrador creada correctamente');
        navigate(`/factura/${invoice!.id}/rectificativa/${newRectificationInvoiceId}`);
      } catch (e: any) {
        const httpExceptionDto = e.response.data as HttpExceptionDto;
        myToastr.error(httpExceptionDto.message);
      }
    }
  };

  const deleteInvoice = async () => {
    try {
      setRequesting(true);
      await api.deleteInvoice(rectificationInvoice!.id);
      myToastr.success('Factura rectificativa eliminada');
      navigate('/facturas');
    } catch (e) {
      setRequesting(false);
      myToastr.error('Error al eliminar la factura rectificativa');
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!invoice || (rectificationId && !rectificationInvoice)) {
    return <div>Error: No se pudo cargar la factura</div>;
  }

  return (
    <>
      <div className="invoice-view">
        <div className="d-flex flex-row align-items-center invoice-view-header gap-2">
          <Link to="/facturas">
            <ChevronLeft className="me-1" size={16} />
            Volver
          </Link>
          <h1 className="flex-grow-1">{`Factura rectificativa de #${invoice.internalId}`}</h1>
        </div>
        <div className="white-container">
          <div className="col-4">
            <div className="d-flex flex-column">
              <label>Fecha:</label>
              <input
                {...register('date', { required: 'La fecha es obligatoria' })}
                className={clsx('form-control', { 'is-invalid': errors.date })}
                type="date"
                // value={extendedInvoiceDto.date || ''}
                // onChange={(e) => setExtendedInvoiceDto({ ...extendedInvoiceDto, date: e.target.value })}
                // disabled={extendedInvoiceDto.invoiceStatus === InvoiceStatus.Posted}
              />
            </div>
          </div>
        </div>
        <div className="white-container invoice-product-lines">
          <table>
            <thead>
              <tr>
                <th className="">Producto</th>
                <th className="units text-center">Unidades</th>
                <th className="amount text-center">
                  <div className="d-flex flex-column">
                    <span>Precio sin descuento €</span>
                  </div>
                </th>
                <th className="discount-product text-center">Desc. %</th>
                <th className="amount text-center">Precio con descuento €</th>
                <th className="taxBase text-center">Subtotal €</th>
                <th className="vatAmount text-center">IVA €</th>
                {EQUIVALENCE_SURCHARGE > 0 && <th className="equivalenceSurcharge text-center">Recargo de equi. €</th>}
                <th className="total text-end">Total</th>
              </tr>
            </thead>
            <tbody>
              {productFields.map((field, index) => {
                const units = products?.[index]?.units || 0;
                const invoiceProduct = invoice.invoiceProducts.find((p) => p.id === field.dbId)!;
                const discount = invoiceProduct.discount || 0;
                const taxBase = units * invoiceProduct.amount * (1 - discount / 100);
                const discountedPrice = invoiceProduct.amount * (1 - discount / 100);
                const vatAmount = (taxBase * (invoiceProduct.vatPercentage || 0)) / 100;
                const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
                const total = taxBase + vatAmount + equivalenceSurchargeAmount;
                return (
                  <tr key={index}>
                    <td>{invoiceProduct.product!.name}</td>
                    <td>
                      <input
                        {...register(`products.${index}.units`, {
                          valueAsNumber: true,
                          validate: (value) => {
                            if (Number.isNaN(value)) {
                              return true;
                            }
                            if (value < 0) {
                              return 'Debes rectificar al menos 1 unidad';
                            }
                            if (value > field.maxUnits) {
                              return field.maxUnits === 1 ? 'No puedes rectificar más de 1 unidad' : `No puedes rectificar más de ${field.maxUnits} unidades`;
                            }
                            return true;
                          },
                        })}
                        className={clsx({ 'is-invalid': errors.products?.[index]?.units })}
                        type="number"
                        max={field.maxUnits}
                        min={1}
                        placeholder="Uds."
                      />
                      {errors.products?.[index]?.units && <div className="invalid-feedback">{errors.products[index]?.units?.message}</div>}
                    </td>
                    <td className="text-center">{formatNumber(invoiceProduct.amount)}€</td>
                    <td className="text-center">{formatNumber(invoiceProduct.discount)}%</td>
                    <td className="text-center">{formatNumber(discountedPrice)}€</td>
                    <td className="text-center">{formatNumber(taxBase)}€</td>
                    <td className="text-center">{formatNumber(vatAmount)}€</td>
                    {EQUIVALENCE_SURCHARGE > 0 && <td className="text-center">{formatNumber(equivalenceSurchargeAmount)}€</td>}
                    <td className="text-end">{formatNumber(total)}€</td>
                  </tr>
                );
              })}
              {manualProductFields.map((field, index) => {
                const units = manualProducts?.[index]?.units || 0;
                const manualInvoiceProductDto = invoice.manualInvoiceProducts.find((p) => p.id === field.dbId)!;
                const discount = manualInvoiceProductDto.discount || 0;
                const taxBase = units * manualInvoiceProductDto.amount * (1 - discount / 100);
                const discountedPrice = manualInvoiceProductDto.amount * (1 - discount / 100);
                const vatAmount = (taxBase * (manualInvoiceProductDto.vatPercentage || 0)) / 100;
                const equivalenceSurchargeAmount = (taxBase * EQUIVALENCE_SURCHARGE) / 100;
                const total = taxBase + vatAmount + equivalenceSurchargeAmount;
                return (
                  <tr key={index}>
                    <td>{manualInvoiceProductDto.name}</td>
                    <td className="text-center">
                      <input
                        {...register(`manualProducts.${index}.units`, {
                          valueAsNumber: true,
                          validate: (value) => {
                            if (Number.isNaN(value)) {
                              return true;
                            }
                            if (value < 0) {
                              return 'Debes rectificar al menos 1 unidad';
                            }
                            if (value > field.maxUnits) {
                              return field.maxUnits === 1 ? 'No puedes rectificar más de 1 unidad' : `No puedes rectificar más de ${field.maxUnits} unidades`;
                            }
                            return true;
                          },
                        })}
                        className={clsx({ 'is-invalid': errors.manualProducts?.[index]?.units })}
                        max={field.maxUnits}
                        min={1}
                        placeholder="Uds."
                        type="number"
                      />
                      {errors.manualProducts?.[index]?.units && <div className="invalid-feedback">{errors.manualProducts[index]?.units?.message}</div>}
                    </td>
                    <td className="text-center">{formatNumber(manualInvoiceProductDto.amount)}€</td>
                    <td className="text-center">{formatNumber(manualInvoiceProductDto.discount)}%</td>
                    <td className="text-center">{formatNumber(discountedPrice)}€</td>
                    <td className="text-center">{formatNumber(taxBase)}€</td>
                    <td className="text-center">{formatNumber(vatAmount)}€</td>
                    <td className="text-center">{formatNumber(equivalenceSurchargeAmount)}€</td>
                    <td className="text-end">{formatNumber(total)}€</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-end">
          <div className="invoice-totals">
            <table>
              <thead>
                <tr>
                  <th>Uds. total</th>
                  <th>Base imponible</th>
                  <th>IVA total</th>
                  <th>Recargo de equiv.</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input type="text" readOnly disabled value={invoiceTotals.units} />
                  </td>
                  <td>
                    <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.taxBase)}€`} />
                  </td>
                  <td>
                    <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.vat)}€`} />
                  </td>
                  <td>
                    <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.equivalenceSurcharge)}€`} />
                  </td>
                  <td>
                    <input type="text" readOnly disabled value={`${formatNumber(invoiceTotals.total)}€`} />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="d-flex justify-content-between">
          <div>
            {rectificationInvoice?.invoiceStatus === InvoiceStatus.Pending && (
              <button className="outlined" onClick={() => setShowConfirmModal(true)}>
                Eliminar
              </button>
            )}
          </div>
          <div>
            {(!rectificationInvoice || rectificationInvoice.invoiceStatus === InvoiceStatus.Pending) && (
              <button className="me-3 outlined" disabled={requesting} onClick={() => onSave(InvoiceStatus.Posted)}>
                Contabilizar
              </button>
            )}
            {(!rectificationInvoice || rectificationInvoice.invoiceStatus === InvoiceStatus.Pending) && (
              <button className="filled" disabled={requesting} onClick={() => onSave(InvoiceStatus.Pending)}>
                Guardar
              </button>
            )}
          </div>
        </div>
      </div>
      <ConfirmModal
        acceptButtonClass="accept-button"
        show={showConfirmModal}
        title="Eliminar Factura"
        content="¿Estás seguro que quieres eliminar la factura rectificativa?"
        closeModal={(result: boolean) => {
          setShowConfirmModal(false);
          if (result) {
            deleteInvoice();
          }
        }}
      />
    </>
  );
};

export default NewRectificationInvoiceView;
